import React from "react";
//HOOKS
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
//SLICES
import { selectLabels } from "../../slices/language";
import { selectUser, selectUserRole } from "../../slices/user";
//UTILS
import { showError } from "../../utils/helpers";
//COMPONENTS
import DesktopHeader from "../../components/DesktopHeader";
import DesktopBody from "../../components/DesktopBody";
import TableComponent from "../../components/TableComponent";
import { TableData } from "../../components/TableComponent/TableComponent";
import { DesktopFiltersProps } from "../../components/DesktopFilters/DesktopFilters";
import DesktopDropView from "../../components/DesktopDropView";
import NewUserForm from "../../components/NewUserForm";
import DeleteUserModal from "../../components/Modals/DeleteUserModal";
import ResendConfirmationEmailModal from "../../components/Modals/ResendConfirmationEmailModal";
//API
import api, { CreateUserPayload } from "../../api";
//TYPES
import { User } from "../../api/models/user-model";
import { PropertyArray } from "../../api/models/property-model";
//MATERIAL
import { CircularProgress } from "@mui/material";
//DAYJS
import dayjs from "dayjs";

const NetworkAdmins = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const dispatch = useAppDispatch();

  const headers = [
    { label: "ID", key: "ID" },
    { label: labels.name, key: labels.name },
    { label: labels.telephone, key: labels.telephone },
    { label: labels.email, key: labels.email },
    { label: labels.created_by, key: labels.created_by },
  ];

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [tableDataToDisplay, setTableDataToDisplay] =
    React.useState<TableData | null>(null);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [newUser, setNewUser] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalUsers, setTotalUsers] = React.useState<number>(0);
  const [userToResend, setUserToResend] = React.useState<User | null>(null);
  const [isResendingEmail, setIsResendingEmail] = React.useState(false);
  const [selectedProperty, setSelectedProperty] = React.useState<string | null>(
    null
  );
  const [properties, setProperties] = React.useState<PropertyArray | null>(
    null
  );
  const [dataTable, setDataTable] = React.useState<any[]>([]);
  const [showDeleteUserModal, setShowDeleteUserModal] =
    React.useState<boolean>(false);
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);
  const [userToDelete, setUserToDelete] = React.useState<User | null>(null);

  const debouncedSearchText = useDebounce(searchFilter, 500);

  const companyId = user?.company?.id;

  const fixedPage = React.useMemo(() => {
    return page + 1;
  }, [page]);

  const accessPointsArray = user?.access_points
    ? user.access_points.map((item) => {
        return {
          label: item?.name,
          value: `${item.id}`,
        };
      })
    : [];

  accessPointsArray.unshift({
    label: labels.all_access_points,
    value: "0",
  });

  const accessPointsForNewUserProp =
    properties?.data
      .filter((item) => item?.id === Number(selectedProperty))[0]
      .access_points?.map((accPoint) => accPoint?.id) || [];

  const propertiesArray = properties
    ? properties.data.map((item) => {
        return {
          label: item?.name || "",
          value: `${item.id}`,
        };
      })
    : [];

  const selectedPropertyName =
    properties?.data.filter((item) => item?.id === Number(selectedProperty))[0]
      ?.name || "";

  const currentDate = dayjs().format("DD-MM-YY");

  const filters: DesktopFiltersProps = {
    filterData:
      userRole === "ciso"
        ? [
            {
              type: "selector",
              value: selectedProperty,
              setValue: setSelectedProperty,
              disabled: isLoading,
              tooltip: "",
              function: () => {},
              array: propertiesArray,
              size: "small",
              icon: "",
              label: labels.access_points,
              multiDateValue: null,
              setMultiDateValue: () => {},
              filterWidth: 350,
            },
            {
              type: "search",
              value: searchFilter,
              setValue: setSearchFilter,
              disabled: isLoading,
              tooltip: "",
              function: () => {},
              array: [],
              size: "small",
              icon: "",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              filterWidth: 350,
            },
            {
              type: "function",
              value: "",
              setValue: () => {},
              disabled: isLoading,
              tooltip: labels.download_content,
              function: () => {},
              array: [],
              size: "small",
              icon: "fa-solid:file-download",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              downloadable: true,
              downloadData: dataTable,
              downloadHeader: headers,
              downloadFilename: `${labels.admins} ${currentDate}`,
            },
            {
              type: "function",
              value: "",
              setValue: () => {},
              disabled: isLoading,
              tooltip: "",
              function: () => setNewUser(true),
              array: [],
              size: "small",
              icon: "fa6-solid:circle-plus",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
            },
          ]
        : [
            {
              type: "search",
              value: searchFilter,
              setValue: setSearchFilter,
              disabled: isLoading,
              tooltip: "",
              function: () => {},
              array: [],
              size: "small",
              icon: "",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              filterWidth: 350,
            },
            {
              type: "function",
              value: "",
              setValue: () => {},
              disabled: isLoading,
              tooltip: labels.download_content,
              function: () => {},
              array: [],
              size: "small",
              icon: "fa-solid:file-download",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
              downloadable: true,
              downloadData: dataTable,
              downloadHeader: headers,
              downloadFilename: `${labels.admins} ${currentDate}`,
            },
            {
              type: "function",
              value: "",
              setValue: () => {},
              disabled: isLoading,
              tooltip: "",
              function: () => setNewUser(true),
              array: [],
              size: "small",
              icon: "fa6-solid:circle-plus",
              label: "",
              multiDateValue: null,
              setMultiDateValue: () => {},
            },
          ],
  };

  const getUsers = React.useCallback(
    async (debouncedSearchText: string) => {
      try {
        setIsLoading(true);
        let response;
        let responseTotal;
        if (userRole === "ciso") {
          if (selectedProperty) {
            responseTotal = await api.usersListRequest(
              debouncedSearchText,
              Number(selectedProperty),
              null,
              "property_admin"
            );
            response = await api.usersListPaginationRequest(
              debouncedSearchText,
              Number(selectedProperty),
              null,
              "property_admin",
              "",
              "",
              fixedPage,
              rowsPerPage
            );
          }
        } else {
          responseTotal = await api.usersListRequest(
            debouncedSearchText,
            user?.property?.id,
            null,
            "property_admin"
          );
          response = await api.usersListPaginationRequest(
            debouncedSearchText,
            user?.property?.id,
            null,
            "property_admin",
            "",
            "",
            fixedPage,
            rowsPerPage
          );
        }
        if (response && responseTotal) {
          setTotalUsers(responseTotal.length);
          const dataForDownloadableTable = response.map((item) => {
            return {
              ID: `${item?.id}`,
              [labels.name]: `${item?.name}`,
              [labels.telephone]: `${item?.phone}`,
              [labels.email]: `${item?.email}`,
              [labels.created_by]: `${item?.created_by_users[0]?.name}`,
            };
          });
          setDataTable(dataForDownloadableTable);
          const dataForTable: TableData = {
            header: [
              {
                label: labels.name,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.email,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.telephone,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.created_by,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: labels.status,
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: "",
                isIcon: false,
                tooltipLabel: "",
              },
              {
                label: "",
                isIcon: false,
                tooltipLabel: "",
              },
            ],
            body: response.map((item) => {
              return {
                row: item.id,
                cells: [
                  {
                    label: [
                      {
                        mainLabel: item?.name,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-row",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.email,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-row",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.phone,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-col",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.created_by_users[0]?.name,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-col",
                    isButton: false,
                  },
                  {
                    label: [
                      {
                        mainLabel: item?.confirmed
                          ? item?.blocked
                            ? labels.blocked
                            : labels.active
                          : labels.pending,
                        tooltipLabel: "",
                      },
                    ],
                    isIcon: false,
                    display: "flex-dir-col",
                    isButton: false,
                    color: item?.confirmed
                      ? item?.blocked
                        ? "#c2170e"
                        : "#15ab0f"
                      : "",
                    secondaryButton: !item?.confirmed
                      ? {
                          label: labels.resend_confirmation_email,
                          buttonFunction: () => setUserToResend(item),
                        }
                      : undefined,
                  },
                  {
                    label: [
                      {
                        mainLabel: "ic:round-edit",
                        tooltipLabel: labels.edit,
                      },
                    ],
                    isIcon: true,
                    display: "flex-dir-row",
                    isButton: false,
                    buttonFunction: () => {
                      setSelectedUser(item);
                      setNewUser(true);
                    },
                    disabledButton: !item?.confirmed,
                  },
                  {
                    label: [
                      {
                        mainLabel: "material-symbols-light:delete",
                        tooltipLabel: labels.delete,
                      },
                    ],
                    isIcon: true,
                    display: "flex-dir-row",
                    isButton: false,
                    buttonFunction: () => {
                      setUserToDelete(item);
                      setShowDeleteUserModal(true);
                    },
                    disabledButton: !item?.confirmed,
                  },
                ],
              };
            }),
          };
          setTableDataToDisplay(dataForTable);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dispatch,
      fixedPage,
      labels.active,
      labels.blocked,
      labels.created_by,
      labels.delete,
      labels.edit,
      labels.email,
      labels.generic_error,
      labels.name,
      labels.pending,
      labels.resend_confirmation_email,
      labels.status,
      labels.telephone,
      rowsPerPage,
      selectedProperty,
      user?.property?.id,
      userRole,
    ]
  );

  React.useEffect(() => {
    if (userRole !== "ciso") {
      if (user?.property?.id) {
        getUsers(debouncedSearchText);
      }
    } else {
      getUsers(debouncedSearchText);
    }
  }, [debouncedSearchText, getUsers, user?.property?.id, userRole]);

  React.useEffect(() => {
    getProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const getProperties = async () => {
    if (companyId) {
      try {
        setIsLoading(true);
        const propertyResponse = await api.propertiesByCompanyRequest(
          companyId
        );
        if (propertyResponse) {
          setProperties(propertyResponse);
          setSelectedProperty(`${propertyResponse?.data[0]?.id}`);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const deleteUser = async () => {
    if (userToDelete) {
      try {
        setIsLoading(true);
        const formattedDate = dayjs().format("YYYY-MM-DD");
        const payload: CreateUserPayload = {
          deleted_at: formattedDate,
        };
        const response = await api.updateUser(userToDelete?.id, payload);
        if (response) {
          setShowDeleteUserModal(false);
          setUserToDelete(null);
          getUsers(debouncedSearchText);
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  const resendConfirmationEmail = async () => {
    if (!userToResend?.email) return;

    try {
      setIsResendingEmail(true);
      await api.resendConfirmationEmail({
        email: userToResend.email,
      });
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      throw error;
    } finally {
      setIsResendingEmail(false);
    }
  };

  return (
    <div className="w-100">
      <DeleteUserModal
        showDeleteUserModal={showDeleteUserModal}
        setShowDeleteUserModal={setShowDeleteUserModal}
        deleteFunction={deleteUser}
        userToDelete={userToDelete}
        setUserToDelete={setUserToDelete}
      />
      <ResendConfirmationEmailModal
        open={!!userToResend}
        onClose={() => setUserToResend(null)}
        onConfirm={resendConfirmationEmail}
        user={userToResend}
        isLoading={isResendingEmail}
      />
      <DesktopHeader title={labels.admins} />
      <DesktopBody hasFilters={true} filterData={filters.filterData}>
        <div className="w-100 h-100 px-8 pb-8 border-box">
          {isLoading ? (
            <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
              <CircularProgress />
            </div>
          ) : (
            <TableComponent
              selectableRow={false}
              data={tableDataToDisplay}
              onPressRow={() => {}}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              total={totalUsers}
              tableHeight={"calc(100vh - 188px)"}
              emptyMessage={labels.no_admins_registered}
            />
          )}
        </div>
      </DesktopBody>
      <DesktopDropView
        showView={newUser}
        setShowView={setNewUser}
        content={
          <div className="flex h-100 border-box">
            <div className="h-100 border-box p-8 scroll">
              {userRole === "ciso" ? (
                <NewUserForm
                  roleToCreate={6}
                  propertyIdProp={Number(selectedProperty)}
                  propertyNameProp={selectedPropertyName}
                  accessPointsProp={accessPointsForNewUserProp}
                  showForm={newUser}
                  setShowForm={setNewUser}
                  extraFunc={() => getUsers(debouncedSearchText)}
                  isMobile={false}
                />
              ) : (
                <NewUserForm
                  roleToCreate={6}
                  showForm={newUser}
                  setShowForm={setNewUser}
                  extraFunc={() => getUsers(debouncedSearchText)}
                  selectedUser={selectedUser}
                  setSelectedUser={setSelectedUser}
                  isMobile={false}
                />
              )}
            </div>
          </div>
        }
        extraFunc={false}
        title={selectedUser ? labels.edit_admin : labels.new_admin}
      />
    </div>
  );
};

export default NetworkAdmins;
